<script setup>
/* eslint-disable vue/no-mutating-props -- conditions array is owned by parent form state */
import { ref, useTemplateRef } from 'vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';

defineProps({
  conditions: { type: Array, required: true },
  filterTypes: { type: Array, required: true },
  hasErrors: { type: Boolean, default: false },
});

const emit = defineEmits(['append', 'remove']);

const expanded = ref(true);
const conditionsRef = useTemplateRef('conditionsRef');

const validate = () => {
  const refs = conditionsRef.value;
  if (!refs) return true;
  const list = Array.isArray(refs) ? refs : [refs];
  return list.every(condition => condition.validate());
};

const resetValidation = () => {
  const refs = conditionsRef.value;
  if (!refs) return;
  const list = Array.isArray(refs) ? refs : [refs];
  list.forEach(c => c.resetValidation());
};

defineExpose({ validate, resetValidation });
</script>

<template>
  <section
    class="flex flex-col gap-3"
    :class="
      hasErrors
        ? 'rounded-xl outline outline-1 -outline-offset-1 outline-n-ruby-5'
        : ''
    "
  >
    <button
      type="button"
      class="flex w-full items-center gap-2 text-start"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AUTOMATION.FLOW.TRIGGER_CONDITIONS.TITLE') }}
      </span>
      <span
        v-tooltip.top="$t('AUTOMATION.FLOW.TRIGGER_CONDITIONS.INFO_TOOLTIP')"
        class="inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-n-slate-9 text-[10px] text-n-solid-1 i-lucide-info"
        role="img"
        :aria-label="$t('AUTOMATION.FLOW.TRIGGER_CONDITIONS.INFO_TOOLTIP')"
        @click.stop
      />
      <span
        class="ms-auto inline-flex size-5 shrink-0 items-center justify-center text-n-slate-12 transition-transform duration-200"
        :class="[expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down']"
        aria-hidden="true"
      />
    </button>

    <div v-show="expanded" class="flex flex-col gap-3">
      <ul class="m-0 flex list-none flex-col gap-4 p-0">
        <template v-for="(_condition, i) in conditions" :key="i">
          <ConditionRow
            v-if="i === 0"
            ref="conditionsRef"
            v-model:attribute-key="conditions[i].attribute_key"
            v-model:filter-operator="conditions[i].filter_operator"
            v-model:values="conditions[i].values"
            layout="workflow"
            :filter-types="filterTypes"
            :show-query-operator="false"
            @remove="emit('remove', i)"
          />
          <ConditionRow
            v-else
            ref="conditionsRef"
            v-model:attribute-key="conditions[i].attribute_key"
            v-model:filter-operator="conditions[i].filter_operator"
            v-model:query-operator="conditions[i - 1].query_operator"
            v-model:values="conditions[i].values"
            layout="workflow"
            :filter-types="filterTypes"
            show-query-operator
            @remove="emit('remove', i)"
          />
        </template>
      </ul>

      <button
        type="button"
        class="inline-flex w-fit items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2 dark:border-n-strong dark:bg-n-solid-2 dark:hover:bg-n-alpha-3"
        @click="emit('append')"
      >
        <span class="i-lucide-plus size-4 shrink-0" aria-hidden="true" />
        {{ $t('AUTOMATION.FLOW.TRIGGER_CONDITIONS.ADD_FILTER') }}
      </button>
    </div>
  </section>
</template>
